import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ShieldCheck, Clock, Activity, Building2, Tag, Ticket, Sparkles,
    Search, AlertTriangle, Loader2, Inbox, Phone,
} from "lucide-react";
import { membersApi } from "@/lib/api/directory";
import { visitsApi } from "@/lib/api/visits";
import { useI18n } from "@/contexts/I18nContext";

// ── Helpers ──────────────────────────────────────────────────────────────────
const TIER_STYLES = {
    SILVER:   { bg: "bg-zinc-100 dark:bg-zinc-800",   text: "text-zinc-700 dark:text-zinc-300",   label: { en: "Silver",   ar: "فضي"     } },
    GOLD:     { bg: "bg-amber-50 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-400", label: { en: "Gold",     ar: "ذهبي"    } },
    PLATINUM: { bg: "bg-sky-50 dark:bg-sky-900/30",   text: "text-sky-700 dark:text-sky-400",     label: { en: "Platinum", ar: "بلاتيني" } },
};

function fmtDate(iso, lang) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB", {
            day: "numeric", month: "short", year: "numeric",
        });
    } catch (_) { return iso.slice(0, 10); }
}

// ── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, iconBg, iconColor, label, value, sub, subColor }) {
    return (
        <div className="rounded-2xl border border-border bg-card p-5 flex items-start gap-4">
            <div className={`h-11 w-11 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
                <Icon className={`h-5 w-5 ${iconColor}`} />
            </div>
            <div className="min-w-0">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
                <p className="text-2xl font-bold tracking-tight mt-0.5">{value}</p>
                {sub && <p className={`text-xs mt-0.5 ${subColor || "text-muted-foreground"}`}>{sub}</p>}
            </div>
        </div>
    );
}

// ── Recent visit row ─────────────────────────────────────────────────────────
function VisitRow({ visit, lang }) {
    const offerTitle = lang === "ar"
        ? (visit.offer_title_ar || visit.offer_title_en)
        : (visit.offer_title_en || visit.offer_title_ar);

    return (
        <div className="flex items-center gap-4 py-3 border-b border-border/50 last:border-0">
            <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Activity className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
                {offerTitle ? (
                    <p className="text-sm font-medium truncate">{offerTitle}</p>
                ) : (
                    <p className="text-sm font-medium text-muted-foreground italic">
                        {lang === "ar" ? "زيارة فقط" : "Visit only"}
                    </p>
                )}
                <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                    {visit.provider_name && (
                        <>
                            <Building2 className="h-3 w-3 shrink-0" />
                            <span className="truncate">{visit.provider_name}</span>
                        </>
                    )}
                </div>
            </div>
            <p className="text-xs text-muted-foreground shrink-0 tabular-nums">
                {fmtDate(visit.visited_at, lang)}
            </p>
        </div>
    );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function MemberDashboardPage() {
    const { lang } = useI18n();
    const tr = (en, ar) => lang === "ar" ? ar : en;

    const [member, setMember]   = useState(null);
    const [visits, setVisits]   = useState(null);
    const [claims, setClaims]   = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            membersApi.me(),
            visitsApi.myHistory({ limit: 5, offset: 0 }),
            membersApi.listClaims().catch(() => null),
        ])
            .then(([m, v, c]) => { setMember(m); setVisits(v); setClaims(c); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[50vh]">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    const tier      = member?.tier || "SILVER";
    const tierStyle = TIER_STYLES[tier] || TIER_STYLES.SILVER;
    const tierLabel = tierStyle.label[lang];

    const daysLeft      = member?.days_to_expiry;
    const isExpired     = member?.is_expired;
    const isExpiring    = member?.is_expiring_soon;
    const isPending     = member?.activation_status !== "ACTIVE";
    const totalVisits   = visits?.total ?? 0;
    const recentVisits  = visits?.items ?? [];
    const totalSavings  = Math.round(claims?.total_savings ?? 0);
    const claimedCount  = claims?.claimed_count ?? 0;
    const activeClaims  = (claims?.items || []).filter((c) => (c.status || "CLAIMED") === "CLAIMED").slice(0, 3);
    const hasEstimatedSavings = !!claims?.savings_includes_estimates;

    // Days remaining display
    let daysLabel = "";
    let daysColor = "text-muted-foreground";
    if (isExpired) {
        daysLabel = tr("Expired", "منتهية الصلاحية");
        daysColor = "text-destructive";
    } else if (isExpiring) {
        daysLabel = tr(`${daysLeft} days left — renew soon`, `${daysLeft} يوم متبقٍ — جدد قريباً`);
        daysColor = "text-amber-600 dark:text-amber-400";
    } else if (daysLeft != null) {
        daysLabel = tr(`${daysLeft} days remaining`, `${daysLeft} يوم متبقٍ`);
    }

    return (
        <div className="max-w-4xl space-y-8 fade-up" data-testid="page-member-dashboard">

            {/* Header */}
            <header>
                <p className="text-sm font-medium text-primary">
                    {tr("Your membership", "عضويتك")}
                </p>
                <h1 className="mt-1 text-3xl font-bold tracking-tight">
                    {tr("Welcome back", "مرحباً بعودتك")}
                    {member?.full_name ? `, ${member.full_name.split(" ")[0]}` : ""}
                </h1>
                <p className="mt-1.5 text-sm text-muted-foreground">
                    {tr("Here is your membership overview.", "إليك نظرة عامة على عضويتك.")}
                </p>
            </header>

            {/* Pending activation banner */}
            {isPending && (
                <div className="rounded-2xl border border-amber-400/40 bg-amber-50 dark:bg-amber-950/20 px-5 py-4 flex items-start gap-4">
                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-amber-800 dark:text-amber-400">
                            {tr("Payment verification in progress", "جارٍ التحقق من الدفع")}
                        </p>
                        <p className="text-sm text-amber-700 dark:text-amber-500 mt-0.5">
                            {member?.payment_reference
                                ? tr(`Reference: ${member.payment_reference} · Usually takes 2–12 hours.`, `المرجع: ${member.payment_reference} · عادةً يستغرق ٢–١٢ ساعة.`)
                                : tr("Usually takes 2–12 hours.", "عادةً يستغرق ٢–١٢ ساعة.")}
                        </p>
                    </div>
                    <Link
                        to="/member/pending"
                        className="text-xs font-semibold text-amber-700 dark:text-amber-400 hover:underline shrink-0 mt-0.5"
                    >
                        {tr("View status →", "عرض الحالة →")}
                    </Link>
                </div>
            )}

            {/* Stats row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    icon={Sparkles}
                    iconBg="bg-emerald-50 dark:bg-emerald-950/30"
                    iconColor="text-emerald-600 dark:text-emerald-400"
                    label={tr("Total Savings", "إجمالي التوفير")}
                    value={`${totalSavings.toLocaleString(lang === "ar" ? "ar-EG" : "en-US")} ${tr("EGP", "ج.م")}`}
                    sub={hasEstimatedSavings
                        ? tr("Includes estimates", "يشمل تقديرات")
                        : (totalSavings > 0
                            ? tr("Across claimed deals", "من العروض المحجوزة")
                            : tr("Claim a deal to start saving", "احجز عرضاً للبدء"))}
                />
                <StatCard
                    icon={Ticket}
                    iconBg="bg-primary/10"
                    iconColor="text-primary"
                    label={tr("Deals Claimed", "العروض المحجوزة")}
                    value={claimedCount}
                    sub={claimedCount === 1
                        ? tr("deal in your account", "عرض في حسابك")
                        : tr("deals in your account", "عروض في حسابك")}
                />
                <StatCard
                    icon={Activity}
                    iconBg="bg-sky-50 dark:bg-sky-950/30"
                    iconColor="text-sky-600 dark:text-sky-400"
                    label={tr("Total Visits", "إجمالي الزيارات")}
                    value={totalVisits}
                    sub={totalVisits === 1
                        ? tr("visit recorded", "زيارة مسجلة")
                        : tr("visits recorded", "زيارة مسجلة")}
                />
                <StatCard
                    icon={ShieldCheck}
                    iconBg={tierStyle.bg}
                    iconColor={tierStyle.text}
                    label={tr("Membership Tier", "مستوى العضوية")}
                    value={tierLabel}
                    sub={daysLabel}
                    subColor={daysColor}
                />
            </div>

            {/* Active claims rail */}
            {activeClaims.length > 0 && (
                <div className="rounded-2xl border border-border bg-card overflow-hidden">
                    <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                        <div>
                            <h2 className="text-base font-semibold inline-flex items-center gap-2">
                                <Ticket className="h-4 w-4 text-primary" />
                                {tr("Ready to redeem", "جاهز للاستخدام")}
                            </h2>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {tr("Call the clinic and mention your member code", "اتصل بالعيادة واذكر كود عضويتك")}
                            </p>
                        </div>
                        <Link to="/member/claims" className="text-xs font-semibold text-primary hover:underline">
                            {tr("View all →", "عرض الكل →")}
                        </Link>
                    </div>
                    <div className="divide-y divide-border/50">
                        {activeClaims.map((c) => {
                            const title = lang === "ar"
                                ? (c.offer_title_ar || c.offer_title_en)
                                : (c.offer_title_en || c.offer_title_ar);
                            return (
                                <div key={c.id} className="px-5 py-3 flex items-center gap-3">
                                    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                        <Ticket className="h-4 w-4 text-primary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{title || "—"}</p>
                                        <p className="text-xs text-muted-foreground truncate">
                                            {c.provider_name}
                                            {c.discount_amount ? (
                                                <>
                                                    <span className="mx-1.5">•</span>
                                                    <span className="text-emerald-700 font-medium">
                                                        {c.savings_is_estimated && c.discount_type === "PERCENTAGE"
                                                            ? tr(`~${Math.round(c.discount_amount)} EGP est.`, `~${Math.round(c.discount_amount)} ج.م تقديري`)
                                                            : tr(`${Math.round(c.discount_amount)} EGP`, `${Math.round(c.discount_amount)} ج.م`)}
                                                    </span>
                                                </>
                                            ) : null}
                                        </p>
                                    </div>
                                    {c.provider_phone && (
                                        <a
                                            href={`tel:${c.provider_phone}`}
                                            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90"
                                        >
                                            <Phone className="h-3.5 w-3.5" />
                                            {tr("Call", "اتصل")}
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Recent activity */}
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                    <div>
                        <h2 className="text-base font-semibold">
                            {tr("Recent Activity", "النشاط الأخير")}
                        </h2>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {tr("Your latest visits and redeemed deals", "أحدث زياراتك والعروض المستخدمة")}
                        </p>
                    </div>
                    {totalVisits > 5 && (
                        <Link
                            to="/member/visits"
                            className="text-xs font-semibold text-primary hover:underline"
                        >
                            {tr("View all →", "عرض الكل →")}
                        </Link>
                    )}
                </div>

                <div className="px-5">
                    {recentVisits.length === 0 ? (
                        <div className="py-12 flex flex-col items-center gap-3 text-center">
                            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                                <Inbox className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-muted-foreground">
                                    {tr("No visits yet", "لا توجد زيارات بعد")}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {tr("Find a partner provider and show your card to get started.", "ابحث عن مزود شريك وأظهر بطاقتك للبدء.")}
                                </p>
                            </div>
                            <Link
                                to="/member/home"
                                className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
                            >
                                <Search className="h-3.5 w-3.5" />
                                {tr("Find Care", "ابحث عن رعاية")}
                            </Link>
                        </div>
                    ) : (
                        <div>
                            {recentVisits.map((v) => (
                                <VisitRow key={v.id} visit={v} lang={lang} />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Provider instructions card */}
            {!isPending && (
                <div className="rounded-2xl border border-border/50 bg-muted/30 px-5 py-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                        {tr("At the clinic", "في العيادة")}
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        {tr(
                            "Tell the receptionist you are a Hemaia Member and show your digital card. They will verify your membership and apply your discount.",
                            "أخبر المستقبِل بأنك عضو في حماية وأظهر بطاقتك الرقمية. سيتحقق من عضويتك ويطبق خصمك."
                        )}
                    </p>
                </div>
            )}
        </div>
    );
}
